import React, { useEffect, useState, useRef } from 'react';

import PropTypes from 'prop-types';
import { Button, Message, Modal, Tab } from 'semantic-ui-react';

import _isEmpty from 'lodash/isEmpty';
import _isObject from 'lodash/isObject';
import { useDispatch, useSelector } from 'react-redux';

import { deleteBusinessFile, flagBusinessSaved, updateBusinessFile } from 'actions/business';
import CloseableModal from 'components/common/CloseableModal';
import ConfirmModal from 'components/common/ConfirmModal';
import ImageEditor from 'components/modules/files/components/ImageEditor';
import { METADATA_KEY_GUMLET } from 'components/modules/files/constants/business-file-configuration';
import {
  exceedsMaxFileSize,
  getLocalFileSize,
  getScaleFactor,
} from 'components/modules/files/services/size';
import {
  BusinessFilePropType,
  BusinessFileConfigurationPropType,
} from 'components/modules/files/types/files';
import useAsyncEffect from 'components/modules/foundation/hooks/use-async-effect';
import API from 'libs/api';
import { isAdminUser } from 'libs/auth';
import { getErrorMessage } from 'libs/errors';
import ErrorHandler from 'libs/errors/errors';
import { objectToCamelCase } from 'libs/format';
import { selectBusinessId, selectBusinessType } from 'selectors';
import { selectUser } from 'selectors/user';

import {
  TAB_IMAGE_DETAILS_SCALE_FACTOR,
  TAB_NAME_DISPLAY_SETTINGS,
  TAB_NAME_IMAGE_DETAILS,
  TAB_NAME_IMAGE_EDITOR,
} from './FileEditorModal.constants';
import { useImageEditor } from './FileEditorModal.hooks';
import { getIndexForTab } from './FileEditorModal.utils';
import DisplaySettingsForm from './components/DisplaySettingsForm';
import FileDetailsForm from './components/FileDetailsForm';
import { base64ToFile, isRemoteUrl } from '../../services/files';

import './FileEditorModal.scss';

const propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  businessFile: PropTypes.oneOfType([BusinessFilePropType, PropTypes.string]).isRequired,
  businessFileConfiguration: BusinessFileConfigurationPropType,
  onPostSave: PropTypes.func,
  onPostDelete: PropTypes.func,
  hideImageAttributesTab: PropTypes.bool,
  hideImageEditorTab: PropTypes.bool,
};

const defaultProps = {
  businessFileConfiguration: {},
  onPostSave: () => {},
  onPostDelete: () => {},
  hideImageAttributesTab: false,
  hideImageEditorTab: false,
};

export default function FileEditorModal({
  open,
  onClose,
  businessFile: initialBusinessFile,
  businessFileConfiguration: initialBusinessFileConfiguration,
  onPostSave,
  onPostDelete,
  hideImageAttributesTab,
  hideImageEditorTab,
}) {
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const isAdmin = isAdminUser(user);

  const businessId = useSelector(selectBusinessId);
  const businessType = useSelector(selectBusinessType);

  const isBusinessFile = _isObject(initialBusinessFile);
  const {
    id: initialBusinessFileId,
    file: initialFile,
    index: initialBusinessFileIndex,
  } = initialBusinessFile;
  const { id: initialBusinessFileConfigurationId } = initialBusinessFileConfiguration;
  const initialBusinessFileUrl = isBusinessFile ? initialFile : initialBusinessFile;

  const imageEditorRef = useRef(null);
  const [businessFileData, setBusinessFileData] = useState({});
  const [businessFileDataModified, setBusinessFileDataModified] = useState(false);

  const [businessFileConfigurationData, setBusinessFileConfigurationData] = useState({});
  const [businessFileConfigurationDataModified, setBusinessFileConfigurationDataModified] =
    useState(false);

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSaveLargeFileConfirmation, setShowSaveLargeFileConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [editorImageUrl, setEditorImageUrl] = useState(initialBusinessFileUrl);

  const panes = [];

  const {
    getCurrentImage,
    initialize: initializeImageEditor,
    getTouched: isImageEditorTouched,
  } = useImageEditor(initialBusinessFileUrl, imageEditorRef);

  function initializeBusinessFile() {
    if (isBusinessFile) {
      const { id, url, file, description, filename } = initialBusinessFile;
      setBusinessFileData({
        id,
        url,
        file,
        description,
        filename,
      });
    }
  }

  function initializeBusinessFileConfiguration() {
    setBusinessFileConfigurationData(
      objectToCamelCase({ ...(initialBusinessFileConfiguration || {}) }),
    );
  }

  function onImageQueryParamsChange(e, { value: newValue }) {
    setBusinessFileConfigurationData((previousBusinessFileConfigurationData) => {
      return {
        ...previousBusinessFileConfigurationData,
        metadata: {
          ...(previousBusinessFileConfigurationData.metadata || {}),
          [METADATA_KEY_GUMLET]: newValue,
        },
      };
    });
    setBusinessFileConfigurationDataModified(true);
  }

  function reset() {
    initializeBusinessFile();
    initializeBusinessFileConfiguration();
    setError(null);
    setShowDeleteConfirmation(false);
    setIsDeleting(false);
    setIsSaving(false);
    setBusinessFileDataModified(false);
    setBusinessFileConfigurationDataModified(false);
  }

  useEffect(() => {
    initializeBusinessFile();
  }, [initialBusinessFileId, isBusinessFile]);

  useEffect(() => {
    initializeBusinessFileConfiguration();
  }, [initialBusinessFileConfigurationId]);

  useAsyncEffect(async () => {
    const isImageDetailsTab = activeIndex === getIndexForTab(panes, TAB_NAME_IMAGE_DETAILS);
    const isImageEditorTab = activeIndex === getIndexForTab(panes, TAB_NAME_IMAGE_EDITOR);
    const isDisplaySettingsTab = activeIndex === getIndexForTab(panes, TAB_NAME_DISPLAY_SETTINGS);

    if (isImageDetailsTab || isDisplaySettingsTab) {
      const currentImage = await getCurrentImage({ multiplier: TAB_IMAGE_DETAILS_SCALE_FACTOR });
      setEditorImageUrl(currentImage);
    }
    if (isImageEditorTab) {
      initializeImageEditor();
    }
  }, [activeIndex]);

  function onTabChange(e, { activeIndex: newActiveIndex }) {
    setActiveIndex(newActiveIndex);
  }

  function onChange(fieldName, value) {
    setBusinessFileData((prevBusinessFileData) => ({
      ...prevBusinessFileData,
      [fieldName]: value,
    }));
    setBusinessFileDataModified(true);
  }

  function onCancel() {
    reset();
    onClose();
  }

  async function onDeleteFile() {
    setIsDeleting(true);

    try {
      await API.deleteBusinessFile(businessType, businessId, initialBusinessFileId);
      if (onPostDelete) {
        await onPostDelete(initialBusinessFile);
      }

      dispatch(deleteBusinessFile(initialBusinessFileIndex));
      dispatch(flagBusinessSaved(true));

      setShowDeleteConfirmation(false);
      onClose();
    } catch (e) {
      ErrorHandler.capture(e);
      setError(getErrorMessage(e));
    } finally {
      setIsDeleting(false);
    }
  }

  async function saveBusinessFile(localFile = null) {
    if (!isBusinessFile) {
      return;
    }

    const editorTouched = await isImageEditorTouched();
    if (initialBusinessFileId && (businessFileDataModified || editorTouched)) {
      const { filename, description } = businessFileData;
      const businessFilePayload = { filename, description };

      if (localFile) {
        businessFilePayload.file = localFile;
      }

      const { data } = await API.updateBusinessFile(
        businessType,
        businessId,
        initialBusinessFileId,
        businessFilePayload,
      );
      dispatch(updateBusinessFile({ index: initialBusinessFileIndex, fields: data }));
      dispatch(flagBusinessSaved(true));
    }
  }

  async function saveBusinessFileConfiguration() {
    if (!isBusinessFile) {
      return;
    }

    const { id: businessFileConfigurationId, metadata = {} } = businessFileConfigurationData;
    if (businessFileConfigurationId && businessFileConfigurationDataModified) {
      const businessFileConfigurationPayload = { metadata };

      await API.updateBusinessFileConfiguration(
        businessFileConfigurationId,
        businessFileConfigurationPayload,
      );
    }
  }

  async function onSave({ reduceFile = false } = {}) {
    try {
      setIsSaving(true);
      const { filename = 'image' } = businessFileData || {};

      let localFile = null;
      const imageAsDataUrl = await getCurrentImage();
      if (await isImageEditorTouched()) {
        localFile = base64ToFile(imageAsDataUrl, filename);
      }
      if (exceedsMaxFileSize(localFile) && !reduceFile) {
        setShowSaveLargeFileConfirmation(true);
        setIsSaving(false);
        return;
      }

      if (exceedsMaxFileSize(localFile) && reduceFile) {
        const fileSize = getLocalFileSize(localFile);
        const scaleFactor = getScaleFactor(fileSize);
        const scaledImageDataUrl = await getCurrentImage({ multiplier: scaleFactor });
        localFile = base64ToFile(scaledImageDataUrl, filename);
      }

      if (isBusinessFile) {
        // case when using business file object
        await saveBusinessFile(localFile);
        await saveBusinessFileConfiguration();
      }

      if (onPostSave) {
        await onPostSave({ file: localFile, fileConfiguration: businessFileConfigurationData });
      }

      onClose();
    } catch (e) {
      ErrorHandler.capture(e);
      setError(getErrorMessage(e));
    } finally {
      setIsSaving(false);
    }
  }

  function onCancelLargeFileSave() {
    setActiveIndex(getIndexForTab(panes, TAB_NAME_IMAGE_EDITOR));
    setShowSaveLargeFileConfirmation(false);
  }

  async function onConfirmLargeFileSave() {
    await onSave({ reduceFile: true });
  }

  const { description, filename } = businessFileData || {};

  if (!hideImageAttributesTab && isBusinessFile) {
    panes.push({
      menuItem: TAB_NAME_IMAGE_DETAILS,
      pane: (
        <Tab.Pane key="file-details-form-pane">
          <FileDetailsForm
            url={editorImageUrl}
            onChange={onChange}
            description={description}
            filename={filename}
          />
        </Tab.Pane>
      ),
    });
  }

  if (!hideImageEditorTab) {
    panes.push({
      menuItem: TAB_NAME_IMAGE_EDITOR,
      pane: (
        <Tab.Pane key="image-editor-pane">
          <ImageEditor ref={imageEditorRef} imageUrl={initialBusinessFileUrl} />
        </Tab.Pane>
      ),
    });
  }

  const isPlacementEdition = !_isEmpty(initialBusinessFileConfiguration);
  const isRemote = isRemoteUrl(initialBusinessFileUrl);
  if (isPlacementEdition && isAdmin && isRemote) {
    panes.push({
      menuItem: TAB_NAME_DISPLAY_SETTINGS,
      pane: (
        <Tab.Pane key="display-settings-form-pane">
          <DisplaySettingsForm
            businessFile={initialBusinessFileUrl}
            businessFileConfiguration={businessFileConfigurationData}
            onChange={onImageQueryParamsChange}
          />
        </Tab.Pane>
      ),
    });
  }

  return (
    <CloseableModal
      className="file-editor-modal"
      open={open}
      onClose={onClose}
      header="Edit File"
      size="fullscreen"
    >
      <Modal.Content scrolling className="modal-content">
        <Tab
          menu={{ attached: 'top' }}
          panes={panes}
          activeIndex={activeIndex}
          onTabChange={onTabChange}
          renderActiveOnly={false}
        />
      </Modal.Content>
      <Modal.Actions>
        {error && (
          <Message className="modal-error" negative>
            {error}
          </Message>
        )}
        {!isPlacementEdition && isBusinessFile && (
          <Button
            basic
            disabled={isSaving}
            negative
            onClick={() => setShowDeleteConfirmation(true)}
          >
            Delete
          </Button>
        )}
        <Button basic disabled={isSaving} onClick={onCancel}>
          Cancel
        </Button>
        <Button primary onClick={onSave} loading={isSaving} disabled={isSaving}>
          Save
        </Button>
      </Modal.Actions>
      <ConfirmModal
        open={showDeleteConfirmation}
        header="Delete this File?"
        dimmer="inverted"
        confirmButton={
          <Button negative loading={isDeleting} disabled={isDeleting}>
            Delete
          </Button>
        }
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={onDeleteFile}
      />
      <ConfirmModal
        open={showSaveLargeFileConfirmation}
        header="File Is Too Large"
        content="The file size exceeds the limit. If you continue, the file will be downsized before saving. Alternatively, you can return to the image editor without saving."
        dimmer="inverted"
        confirmButton={
          <Button loading={isSaving} disabled={isSaving}>
            Save Image
          </Button>
        }
        cancelButton={
          <Button negative loading={isSaving} disabled={isSaving}>
            Return To Editor
          </Button>
        }
        onCancel={onCancelLargeFileSave}
        onConfirm={onConfirmLargeFileSave}
      />
    </CloseableModal>
  );
}

FileEditorModal.propTypes = propTypes;
FileEditorModal.defaultProps = defaultProps;
